import React, { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import { ConnectMovmoButton } from "@movmo/connect-button";
import type { MovmoRecognitionInfo } from "@movmo/connect-button";
import "@movmo/connect-button/dist/index.css";
import {
    DemoAirline,
    DEMO_AIRLINE_CLIENT_IDS,
    buildRedirectUri,
    isCanonicalOriginFor,
} from "../config/movmoIdentity";
import { useLinkedTraveler } from "../hooks/useLinkedTraveler";
import { useLinkedTravelerState } from "../hooks/linkedTravelerStore";
import {
    linkCompleteWithSession,
    unlinkWithSession,
} from "../services/demoAirlineApi";

/** Router state the connect callbacks pass back after the traveler declines. */
export interface MovmoDeclinedState {
    movmoDeclined?: boolean;
}

export interface MovmoIdentityChipProps {
    airline: DemoAirline;
    /** Passed through to the chip's `scope`; undefined requests the default. */
    scope?: string[];
    /** Notice text colour, so it reads on the skin's background. */
    noticeColor: string;
}

/**
 * The one-click Movmo identity chip ("Checkout faster with Movmo") for a demo
 * airline's LANDING page: never next to the Express Checkout button.
 *
 * ONE self-switching `ConnectMovmoButton` (connect-button 2.1.0): the
 * anonymous chip when `traveler` is null, the recognized chip ("Welcome back,
 * X", "Not you?", "Manage") when the airline's session carries a linked Movmo
 * identity. The SDK owns placement (a fixed slot on `document.body`, left 40 /
 * bottom 28 / z 9999), so this component writes no positioning CSS for the
 * chip. `movmoTraveler` is passed straight through (useLinkedTraveler derives
 * it) and the chip is held back until the session read settles so neither
 * state flashes wrongly.
 * "Not you?" is Movmo's: after it returns, `onSignedOut` clears the airline's
 * own link (DELETE /link, then the linked-traveler store). Renders only on the
 * airline's canonical demo domain (plus localhost), where its OAuth client's
 * redirect_uri is registered.
 *
 * The declined notice is the demo's own element (the SDK has no such notice).
 */

/** Slot geometry the SDK owns (Figma 134:7 / 137:37); mirrored only to stack the notice. */
const SDK_SLOT_LEFT = 40;
const SDK_SLOT_BOTTOM = 28;
/**
 * Chip height in px: 36 (`movmo-h-9` on AnonymousChip and on both
 * RecognizedBanner states). Notice sits 8px above the chip: 28 + 36 + 8 = 72.
 */
const SDK_CHIP_HEIGHT = 36;
const NOTICE_BOTTOM = SDK_SLOT_BOTTOM + SDK_CHIP_HEIGHT + 8;

const MovmoIdentityChip: React.FC<MovmoIdentityChipProps> = ({
    airline,
    scope,
    noticeColor,
}) => {
    const { movmoTraveler, checked, refresh } = useLinkedTraveler(airline);
    const { clearTraveler } = useLinkedTravelerState(airline);
    const location = useLocation();
    const [noticeDismissed, setNoticeDismissed] = useState(false);
    const declined =
        (location.state as MovmoDeclinedState | null)?.movmoDeclined === true;

    // Fires once after Movmo ended ITS session. Returns the promise so the
    // chip stays anonymous until the airline session is actually cleared.
    // unlinkWithSession swallows a 401; any other failure is logged and the
    // local link is cleared anyway.
    const handleSignedOut = useCallback(
        () =>
            unlinkWithSession(airline)
                .catch((err) =>
                    console.error(`[${airline}] unlink after Movmo sign-out failed:`, err),
                )
                .finally(clearTraveler),
        [airline, clearTraveler],
    );

    // The SDK recognized this device (recognition token or FedCM). Link the
    // airline session exactly as the callback does, but with NO redirect_uri,
    // then refresh the linked-traveler store the way landing's GET /me does.
    const handleRecognized = useCallback(
        async (info: MovmoRecognitionInfo) => {
            console.info(
                `[${airline}] recognized (${info.mechanism}, auto=${info.isAutoSelected})`,
            );
            try {
                await linkCompleteWithSession(airline, {
                    code: info.code,
                    code_verifier: info.codeVerifier,
                });
                await refresh();
            } catch (err) {
                console.error(`[${airline}] recognition link failed:`, err);
            }
        },
        [airline, refresh],
    );

    // "Forget this device" is device-scoped in the SDK; the airline also drops
    // its OWN Movmo link (same unlink as "Not you?") so the re-minted demo
    // session does not greet the traveler again. Fail-open: a failed unlink is
    // logged and the local hint is cleared anyway. The Movmo session stays.
    const handleForgotten = useCallback(
        () =>
            unlinkWithSession(airline)
                .catch((err) =>
                    console.error(`[${airline}] unlink after device forget failed:`, err),
                )
                .finally(() => {
                    console.info(`[${airline}] device forgotten`);
                    clearTraveler();
                }),
        [airline, clearTraveler],
    );

    if (!isCanonicalOriginFor(airline)) return null;
    if (!checked) return null;

    return (
        <>
            <ConnectMovmoButton
                clientId={DEMO_AIRLINE_CLIENT_IDS[airline]}
                redirectUri={buildRedirectUri(airline)}
                isProd={false}
                scope={scope}
                traveler={movmoTraveler}
                onSignedOut={handleSignedOut}
                onRecognized={handleRecognized}
                onForgotten={handleForgotten}
                onError={(err) =>
                    console.error(`[${airline}] Movmo connect did not start:`, err)
                }
            />
            {declined && !noticeDismissed && (
                <p
                    role="status"
                    data-testid="movmo-identity-notice"
                    style={{
                        position: "fixed",
                        left: SDK_SLOT_LEFT,
                        bottom: NOTICE_BOTTOM,
                        zIndex: 9999,
                        color: noticeColor,
                        fontSize: 13,
                        margin: 0,
                    }}
                >
                    No problem, nothing was shared. You can connect Movmo any
                    time.{" "}
                    <button
                        type="button"
                        onClick={() => setNoticeDismissed(true)}
                        style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            color: "inherit",
                            textDecoration: "underline",
                            cursor: "pointer",
                        }}
                    >
                        Dismiss
                    </button>
                </p>
            )}
        </>
    );
};

export default MovmoIdentityChip;
